import React, { useState } from 'react';
import { Typography, TypographyType, TypographyVariant } from 'src/components-dummy';
import { VariantFeature } from 'src/components-bl/Experiment/ExperimentForm/VariantPanel/constants';
import { useStepControlsSync } from '../useStepControlsSync';
import { MerchRulesEditor } from './MerchRulesEditor';
import { RankingStrategyEditor } from './RankingStrategyEditor';
import { ViewConfigEditor } from './ViewConfigEditor';
import { VariantsStepProps } from './types';
import { useVariantsStep } from './useVariantsStep';
import './VariantsStep.scss';

export const VariantsStep = (props: VariantsStepProps): JSX.Element => {
  const { shopId, onStepControlsChange } = props;
  const {
    controlVariantId,
    testVariantId,
    testVariant,
    feature,
    surface,
    isProvisioning,
    provisioningError,
    canContinue,
    isSubmitting,
    onSubmit,
    registerSave,
  } = useVariantsStep(props);
  const [, setChangeCount] = useState(0);

  useStepControlsSync({ canContinue, isSubmitting, onSubmit }, onStepControlsChange, [
    canContinue,
    isSubmitting,
    onSubmit,
  ]);

  const renderEditor = (): JSX.Element => {
    if (provisioningError) {
      return (
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
          Setting up your variants failed — see the banner above to retry or delete this experiment
          and start again.
        </Typography>
      );
    }

    if (isProvisioning) {
      return (
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
          Still setting up your variants, this will only take a moment...
        </Typography>
      );
    }

    if (!feature) {
      return (
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
          This is your baseline comparison — Test starts out identical to Control, so there&apos;s
          nothing to review here yet.
        </Typography>
      );
    }

    if (!surface || !controlVariantId || !testVariantId) {
      return (
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
          Loading variants...
        </Typography>
      );
    }

    switch (feature) {
      case VariantFeature.MerchandisingRules:
        return (
          <MerchRulesEditor
            shopId={shopId}
            surface={surface}
            controlVariantId={controlVariantId}
            testVariantId={testVariantId}
            testVariant={testVariant}
            onChangeCountChange={setChangeCount}
            onSaveReady={registerSave}
          />
        );
      case VariantFeature.RankingStrategy:
        return (
          <RankingStrategyEditor
            shopId={shopId}
            surface={surface}
            controlVariantId={controlVariantId}
            testVariantId={testVariantId}
            testVariant={testVariant}
            onChangeCountChange={setChangeCount}
            onSaveReady={registerSave}
          />
        );
      case VariantFeature.RecEnginesGeneralSettings:
        return (
          <ViewConfigEditor
            shopId={shopId}
            surface={surface}
            controlVariantId={controlVariantId}
            testVariantId={testVariantId}
            testVariant={testVariant}
            onChangeCountChange={setChangeCount}
            onSaveReady={registerSave}
          />
        );
      default:
        return (
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
            Editing for this lever isn&apos;t available in this step yet — you can still launch and
            adjust it from the experiment&apos;s variant editor.
          </Typography>
        );
    }
  };

  return (
    <div className='syte-variants-step'>
      <div className='syte-variants-step-header'>
        <Typography type={TypographyType.Heading} variant={TypographyVariant.MediumBold}>
          Set up the variants
        </Typography>
        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-variants-step-subtitle'
        >
          Control is a frozen copy of your live config, nothing to set up. Variant shows only the
          lever you chose, next to the control value.
        </Typography>
      </div>
      <div className='syte-variants-step-body'>{renderEditor()}</div>
    </div>
  );
};
